import React, { useEffect, useState } from 'react';
import { adminApiService } from '../../services/adminApiService';

type LoginType = 'SESSION' | 'JWT';

interface LoginDisplaySettings {
  defaultLoginType: LoginType;
  showRetailLoginTypeSelector: boolean;
  showAdminLoginTypeSelector: boolean;
}

const DEFAULT_SETTINGS: LoginDisplaySettings = {
  defaultLoginType: 'JWT',
  showRetailLoginTypeSelector: true,
  showAdminLoginTypeSelector: true,
};

const AdminLoginDisplaySettings: React.FC = () => {
  const [settings, setSettings] = useState<LoginDisplaySettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApiService.getLoginDisplaySettings();
        if (response.success && response.data) {
          setSettings({
            defaultLoginType: response.data.defaultLoginType === 'SESSION' ? 'SESSION' : 'JWT',
            showRetailLoginTypeSelector: !!response.data.showRetailLoginTypeSelector,
            showAdminLoginTypeSelector: !!response.data.showAdminLoginTypeSelector,
          });
        } else {
          setError(response.error || 'Failed to load login display settings.');
        }
      } catch (err: any) {
        setError(err?.message || 'Failed to load login display settings.');
      } finally {
        setLoading(false);
      }
    };

    void loadSettings();
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await adminApiService.updateLoginDisplaySettings(settings);
      if (response.success && response.data) {
        setSettings({
          defaultLoginType: response.data.defaultLoginType === 'SESSION' ? 'SESSION' : 'JWT',
          showRetailLoginTypeSelector: !!response.data.showRetailLoginTypeSelector,
          showAdminLoginTypeSelector: !!response.data.showAdminLoginTypeSelector,
        });
        setSuccess('Login display settings saved successfully.');
      } else {
        setError(response.error || 'Failed to save login display settings.');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save login display settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="font-nunito text-sm text-gray-600">Loading login display settings...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
      <div>
        <h3 className="text-lg font-nunito font-semibold text-gray-900">Login Display Settings</h3>
        <p className="mt-1 text-sm font-nunito text-gray-600">
          Control the default login type and whether users can see Login Type options on Retail and Admin login pages.
        </p>
      </div>

      <div>
        <label className="block text-sm font-nunito font-semibold text-gray-700 mb-2">Default Login Type</label>
        <div className="flex items-center space-x-6">
          <label className="flex items-center">
            <input
              type="radio"
              name="defaultLoginType"
              value="SESSION"
              checked={settings.defaultLoginType === 'SESSION'}
              onChange={() => setSettings((prev) => ({ ...prev, defaultLoginType: 'SESSION' }))}
              disabled={saving}
              className="h-4 w-4 text-zazoo-accent-pink focus:ring-zazoo-accent-pink border-gray-300"
            />
            <span className="ml-2 text-sm font-nunito text-gray-700">Web (Session)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="defaultLoginType"
              value="JWT"
              checked={settings.defaultLoginType === 'JWT'}
              onChange={() => setSettings((prev) => ({ ...prev, defaultLoginType: 'JWT' }))}
              disabled={saving}
              className="h-4 w-4 text-zazoo-accent-pink focus:ring-zazoo-accent-pink border-gray-300"
            />
            <span className="ml-2 text-sm font-nunito text-gray-700">API (JWT)</span>
          </label>
        </div>
      </div>

      <div className="space-y-3">
        <label className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          <span className="text-sm font-nunito text-gray-700">Show Login Type section on Retail Login page</span>
          <input
            type="checkbox"
            checked={settings.showRetailLoginTypeSelector}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, showRetailLoginTypeSelector: e.target.checked }))
            }
            disabled={saving}
            className="h-4 w-4 text-zazoo-accent-pink focus:ring-zazoo-accent-pink border-gray-300 rounded"
          />
        </label>

        <label className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
          <span className="text-sm font-nunito text-gray-700">Show Login Type section on Admin Login page</span>
          <input
            type="checkbox"
            checked={settings.showAdminLoginTypeSelector}
            onChange={(e) =>
              setSettings((prev) => ({ ...prev, showAdminLoginTypeSelector: e.target.checked }))
            }
            disabled={saving}
            className="h-4 w-4 text-zazoo-accent-pink focus:ring-zazoo-accent-pink border-gray-300 rounded"
          />
        </label>
      </div>

      {error && (
        <div className="p-3 bg-zazoo-red/10 border border-zazoo-red rounded-lg">
          <p className="text-sm font-nunito text-zazoo-red">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-3 bg-zazoo-green/10 border border-zazoo-green rounded-lg">
          <p className="text-sm font-nunito text-zazoo-green">{success}</p>
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => void saveSettings()}
          disabled={saving}
          className="px-6 py-2.5 bg-zazoo-accent-pink text-white rounded-lg font-mukta font-medium text-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default AdminLoginDisplaySettings;
